import { HttpAgent } from '@ag-ui/client';
import type { RunAgentInput } from '@ag-ui/core';
import type { Attachment } from '@langbridge-builder/design-system';
import type { ChatClient, ChatEvent, ChatRunInput } from './types';

export interface AguiChatClientOptions {
  /** Backend origin, e.g. 'https://api.example.com'. Default same-origin (''). */
  apiBase?: string;
  /** LangBridge agent id. */
  agentId: string;
  /** Extra request headers (auth, etc.). */
  headers?: Record<string, string>;
  /**
   * Map user text (+ optional attachments) → the agent's query object (IO
   * contract). Default: { query: text }. Attachments are placed per agent type
   * (deep/react → `__multimodalParts`; workflow → its multimodal key).
   */
  buildQuery?: (text: string, attachments?: Attachment[]) => unknown;
  /**
   * Optional async pre-step run before `buildQuery`: resolve staged attachments
   * (e.g. upload local files to storage and swap inline base64 for public URLs)
   * and return the transformed list. Rejection aborts the run and surfaces as a
   * RUN_ERROR (existing error banner + retry). Omit to send attachments as-is.
   */
  prepareAttachments?: (attachments: Attachment[]) => Promise<Attachment[]>;
  /** Create a session and return its id (threadId). Default: POST /sessions { agentId }. */
  createSession?: (agentId: string) => Promise<string>;
  /**
   * Existing thread to resume — a restored past conversation. When set, the client
   * attaches to this thread instead of creating a new session, so subsequent runs
   * continue that session's checkpointed graph state. Omit for a fresh chat.
   */
  threadId?: string;
  /**
   * Called when a run is aborted, with the current threadId (session). The SSE
   * connection close already tears the run down server-side; use this to also
   * fire an explicit abort (e.g. POST /agents/abort) for robustness.
   */
  onAbort?: (threadId: string | null) => void;
}

async function defaultCreateSession(
  apiBase: string,
  agentId: string,
  headers?: Record<string, string>,
): Promise<string> {
  const res = await fetch(`${apiBase}/sessions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify({ agentId }),
  });
  if (!res.ok) throw new Error(`createSession failed: ${res.status}`);
  const data = (await res.json()) as { sessionId: string };
  return data.sessionId;
}

/**
 * Real transport against a LangBridge AG-UI endpoint. Ported from the reference
 * console's api.ts. Written + type-checked this cycle; runtime integration with a
 * live backend is deferred (verified via MockChatClient). One instance == one
 * conversation (threadId is created lazily and reused; call reset() for a new one).
 */
export class AguiChatClient implements ChatClient {
  private threadId: string | null;

  constructor(private readonly opts: AguiChatClientOptions) {
    // Seed with an existing thread when resuming a restored session; otherwise
    // it's created lazily on the first run.
    this.threadId = opts.threadId ?? null;
  }

  reset(): void {
    this.threadId = this.opts.threadId ?? null;
  }

  async run(
    input: ChatRunInput,
    onEvent: (event: ChatEvent) => void,
    signal: AbortSignal,
  ): Promise<void> {
    if (signal.aborted) return;
    const { apiBase = '', agentId, headers } = this.opts;
    if (!this.threadId) {
      this.threadId = this.opts.createSession
        ? await this.opts.createSession(agentId)
        : await defaultCreateSession(apiBase, agentId, headers);
    }

    const agent = new HttpAgent({
      url: `${apiBase}/agui/agents/${encodeURIComponent(agentId)}`,
      headers,
    });
    // HIL resume (top-level resume[]), A2UI action, or a fresh query.
    let forwardedProps: Record<string, unknown>;
    let messages: RunAgentInput['messages'];
    let resume: Array<{ interruptId: string; status: 'resolved'; payload: unknown }> | undefined;
    if (input.resume) {
      // Resume the paused graph on the same thread; no new user message. Each
      // decision echoes its action's standard interrupt id. status is always
      // 'resolved' (reject also continues the graph); the rich decision rides
      // in `payload` (the backend reverse-maps it to deepagents decisions[]).
      const { decisions, interruptIds = [] } = input.resume;
      resume = decisions.map((payload, i) => ({
        interruptId: interruptIds[i] ?? `interrupt#${i}`,
        status: 'resolved',
        payload,
      }));
      forwardedProps = {};
      messages = [];
    } else if (input.action) {
      forwardedProps = { a2uiAction: input.action };
      messages = [{ id: crypto.randomUUID(), role: 'user', content: input.text }];
    } else {
      // Resolve attachments (e.g. upload → public URLs) before shaping the query.
      let attachments = input.attachments;
      if (attachments?.length && this.opts.prepareAttachments) {
        attachments = await this.opts.prepareAttachments(attachments);
      }
      forwardedProps = {
        query: this.opts.buildQuery?.(input.text, attachments) ?? { query: input.text },
      };
      messages = [{ id: crypto.randomUUID(), role: 'user', content: input.text }];
    }
    const runInput: RunAgentInput = {
      threadId: this.threadId,
      runId: crypto.randomUUID(),
      state: {},
      messages,
      tools: [],
      context: [],
      forwardedProps,
      // `resume` is an AG-UI-standard top-level field (not in the older core
      // RunAgentInput type); spread it in so it's omitted on non-resume runs.
      ...(resume ? { resume } : {}),
    };

    await new Promise<void>((resolve, reject) => {
      let settled = false;
      let sub: { unsubscribe(): void } | undefined;
      const settle = (fn: () => void) => {
        if (settled) return;
        settled = true;
        signal.removeEventListener('abort', onAbort);
        fn();
      };
      const onAbort = () => {
        agent.abortRun();
        sub?.unsubscribe();
        this.opts.onAbort?.(this.threadId);
        settle(resolve);
      };
      signal.addEventListener('abort', onAbort);
      sub = agent.run(runInput).subscribe({
        next: (event) => onEvent(event),
        error: (err: unknown) =>
          settle(() =>
            signal.aborted || (err as Error)?.name === 'AbortError' ? resolve() : reject(err),
          ),
        complete: () => settle(resolve),
      });
    });
  }
}
